// Template-related types for the Integrable frontend
// These are documented with JSDoc for better IDE support

/**
 * @typedef {'AUTHENTICATION' | 'MARKETING' | 'UTILITY'} TemplateCategory
 */

/**
 * @typedef {'PENDING' | 'APPROVED' | 'REJECTED' | 'PAUSED' | 'PENDING_DELETION'} TemplateStatus
 */

/**
 * @typedef {'NAMED' | 'POSITIONAL'} ParameterFormat
 */

/**
 * @typedef {'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT'} HeaderFormat
 */

/**
 * @typedef {'copy_code' | 'phone_number' | 'quick_reply' | 'url'} ButtonType
 */

/**
 * @typedef {Object} HeaderComponent
 * @property {'HEADER'} type - Component type
 * @property {HeaderFormat} format - Header format
 * @property {string} text - Header text (for TEXT format)
 * @property {string} [example] - Example text for parameters
 * @property {string} [url] - Media URL (for IMAGE, VIDEO, DOCUMENT formats)
 */

/**
 * @typedef {Object} BodyComponent
 * @property {'BODY'} type - Component type
 * @property {string} text - Body text with parameters
 * @property {string} [example] - Example text for parameters
 * @property {boolean} [securityRecommendation] - Security recommendation flag (AUTHENTICATION only)
 * @property {number} [codeExpirationMinutes] - Code expiration in minutes (AUTHENTICATION only)
 */

/**
 * @typedef {Object} FooterComponent
 * @property {'FOOTER'} type - Component type
 * @property {string} text - Footer text
 */

/**
 * @typedef {Object} ButtonComponent
 * @property {'BUTTONS'} type - Component type
 * @property {ButtonType} buttonType - Button type
 * @property {string} text - Button text (max 20 chars)
 * @property {string} [url] - URL for URL buttons
 * @property {string} [phoneNumber] - Phone number for PHONE_NUMBER buttons
 * @property {string} [flowId] - Flow ID for FLOW buttons
 * @property {string} [flowToken] - Flow token for FLOW buttons
 * @property {string} [appId] - App ID for APP buttons
 * @property {string} [appPackageName] - App package name for APP buttons
 * @property {string} [appUrl] - App URL for APP buttons
 */

/**
 * @typedef {HeaderComponent | BodyComponent | FooterComponent | ButtonComponent} TemplateComponent
 */

/**
 * @typedef {Object} Template
 * @property {string} _id - Template ID
 * @property {string} name - Template name (max 512 chars, unique per project)
 * @property {string} projectId - Project ID
 * @property {TemplateCategory} category - Template category
 * @property {ParameterFormat} parameterFormat - Parameter format
 * @property {string} language - Language code (default: en_US)
 * @property {TemplateStatus} status - Template status
 * @property {TemplateComponent[]} components - Template components
 * @property {string} [whatsappTemplateId] - WhatsApp template ID (if approved)
 * @property {string} [rejectionReason] - Rejection reason (if rejected)
 * @property {number} editCount - Number of edits this month
 * @property {string} lastEditedAt - Last edit timestamp
 * @property {string} createdAt - Creation timestamp
 * @property {string} updatedAt - Last update timestamp
 * @property {number} __v - Version number
 */

/**
 * @typedef {Object} TemplateCreateData
 * @property {string} name - Template name
 * @property {TemplateCategory} category - Template category
 * @property {ParameterFormat} parameterFormat - Parameter format
 * @property {string} [language] - Language code (default: en_US)
 * @property {TemplateComponent[]} components - Template components
 */

/**
 * @typedef {Object} TemplateUpdateData
 * @property {string} [name] - Template name
 * @property {TemplateCategory} [category] - Template category
 * @property {ParameterFormat} [parameterFormat] - Parameter format
 * @property {string} [language] - Language code
 * @property {TemplateComponent[]} [components] - Template components
 */

/**
 * @typedef {Object} TemplateFilters
 * @property {TemplateCategory} [category] - Filter by category
 * @property {TemplateStatus} [status] - Filter by status
 * @property {string} [search] - Search in name and component text
 * @property {string} [sortBy] - Sort field (name, category, status, createdAt, updatedAt)
 * @property {'asc' | 'desc'} [sortOrder] - Sort order
 * @property {number} [page] - Page number
 * @property {number} [limit] - Items per page
 */

/**
 * @typedef {Object} TemplateStats
 * @property {number} total - Total templates
 * @property {number} approved - Approved templates
 * @property {number} pending - Pending templates
 * @property {number} rejected - Rejected templates
 * @property {number} paused - Paused templates
 * @property {number} pendingDeletion - Pending deletion templates
 * @property {number} authentication - Authentication templates
 * @property {number} marketing - Marketing templates
 * @property {number} utility - Utility templates
 */

/**
 * @typedef {Object} TemplateEditRestrictions
 * @property {boolean} canEdit - Whether template can be edited
 * @property {string} [reason] - Reason if editing is restricted
 * @property {boolean} [isWithin24Hours] - Whether within 24-hour edit limit
 * @property {boolean} [hasReachedEditLimit] - Whether reached monthly edit limit
 * @property {number} [remainingEdits] - Remaining edits this month
 */

/**
 * @typedef {Object} TemplateValidationError
 * @property {string} field - Field name
 * @property {string} message - Error message
 * @property {string} [componentIndex] - Component index for component-specific errors
 */

/**
 * @typedef {Object} TemplateFormData
 * @property {string} name - Template name
 * @property {TemplateCategory} category - Template category
 * @property {ParameterFormat} parameterFormat - Parameter format
 * @property {string} language - Language code
 * @property {TemplateComponent[]} components - Template components
 */

export const TEMPLATE_CATEGORIES = {
  AUTHENTICATION: 'authentication',
  MARKETING: 'marketing',
  UTILITY: 'utility',
};

export const TEMPLATE_STATUSES = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  PAUSED: 'PAUSED',
  PENDING_DELETION: 'PENDING_DELETION',
};

export const PARAMETER_FORMATS = {
  NAMED: 'NAMED',
  POSITIONAL: 'POSITIONAL',
};

export const HEADER_FORMATS = {
  TEXT: 'TEXT',
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
  DOCUMENT: 'DOCUMENT',
};

export const BUTTON_TYPES = {
  COPY_CODE: 'copy_code',
  PHONE_NUMBER: 'phone_number',
  QUICK_REPLY: 'quick_reply',
  URL: 'url',
};

export const COMPONENT_TYPES = {
  HEADER: 'HEADER',
  BODY: 'BODY',
  FOOTER: 'FOOTER',
  BUTTONS: 'BUTTONS',
};

// Status color mapping for UI
export const STATUS_COLORS = {
  [TEMPLATE_STATUSES.PENDING]: 'bg-amber-100 text-amber-800 border-amber-200',
  [TEMPLATE_STATUSES.APPROVED]:
    'bg-emerald-100 text-emerald-800 border-emerald-200',
  [TEMPLATE_STATUSES.REJECTED]: 'bg-rose-100 text-rose-800 border-rose-200',
  [TEMPLATE_STATUSES.PAUSED]: 'bg-gray-100 text-gray-800 border-gray-200',
  [TEMPLATE_STATUSES.PENDING_DELETION]:
    'bg-amber-100 text-amber-800 border-amber-200',
};

// Category color mapping for UI
export const CATEGORY_COLORS = {
  [TEMPLATE_CATEGORIES.AUTHENTICATION]: 'bg-blue-100 text-blue-800',
  [TEMPLATE_CATEGORIES.MARKETING]: 'bg-purple-100 text-purple-800',
  [TEMPLATE_CATEGORIES.UTILITY]: 'bg-green-100 text-green-800',
};

// Default language
export const DEFAULT_LANGUAGE = 'en_US';

// Language options
export const LANGUAGE_OPTIONS = [
  { value: 'af', label: 'Afrikaans' },
  { value: 'sq', label: 'Albanian' },
  { value: 'ar', label: 'Arabic' },
  { value: 'ar_EG', label: 'Arabic (Egypt)' },
  { value: 'ar_AE', label: 'Arabic (UAE)' },
  { value: 'ar_LB', label: 'Arabic (Lebanon)' },
  { value: 'ar_MA', label: 'Arabic (Morocco)' },
  { value: 'ar_QA', label: 'Arabic (Qatar)' },
  { value: 'az', label: 'Azerbaijani' },
  { value: 'be_BY', label: 'Belarusian' },
  { value: 'bn', label: 'Bengali' },
  { value: 'bg', label: 'Bulgarian' },
  { value: 'ca', label: 'Catalan' },
  { value: 'zh_CN', label: 'Chinese (China)' },
  { value: 'zh_HK', label: 'Chinese (Hong Kong)' },
  { value: 'zh_TW', label: 'Chinese (Taiwan)' },
  { value: 'hr', label: 'Croatian' },
  { value: 'cs', label: 'Czech' },
  { value: 'da', label: 'Danish' },
  { value: 'nl', label: 'Dutch' },
  { value: 'en', label: 'English' },
  { value: 'en_GB', label: 'English (UK)' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'et', label: 'Estonian' },
  { value: 'fil', label: 'Filipino' },
  { value: 'fi', label: 'Finnish' },
  { value: 'fr', label: 'French' },
  { value: 'ka', label: 'Georgian' },
  { value: 'de', label: 'German' },
  { value: 'el', label: 'Greek' },
  { value: 'gu', label: 'Gujarati' },
  { value: 'ha', label: 'Hausa' },
  { value: 'he', label: 'Hebrew' },
  { value: 'hi', label: 'Hindi' },
  { value: 'hu', label: 'Hungarian' },
  { value: 'id', label: 'Indonesian' },
  { value: 'ga', label: 'Irish' },
  { value: 'it', label: 'Italian' },
  { value: 'ja', label: 'Japanese' },
  { value: 'kn', label: 'Kannada' },
  { value: 'kk', label: 'Kazakh' },
  { value: 'rw_RW', label: 'Kinyarwanda' },
  { value: 'ko', label: 'Korean' },
  { value: 'ky_KG', label: 'Kyrgyz (Kyrgyzstan)' },
  { value: 'lo', label: 'Lao' },
  { value: 'lv', label: 'Latvian' },
  { value: 'lt', label: 'Lithuanian' },
  { value: 'mk', label: 'Macedonian' },
  { value: 'ms', label: 'Malay' },
  { value: 'ml', label: 'Malayalam' },
  { value: 'mr', label: 'Marathi' },
  { value: 'nb', label: 'Norwegian' },
  { value: 'fa', label: 'Persian' },
  { value: 'pl', label: 'Polish' },
  { value: 'pt_BR', label: 'Portuguese (Brazil)' },
  { value: 'pt_PT', label: 'Portuguese (Portugal)' },
  { value: 'pa', label: 'Punjabi' },
  { value: 'ro', label: 'Romanian' },
  { value: 'ru', label: 'Russian' },
  { value: 'sr', label: 'Serbian' },
  { value: 'sk', label: 'Slovak' },
  { value: 'sl', label: 'Slovenian' },
  { value: 'es', label: 'Spanish' },
  { value: 'es_AR', label: 'Spanish (Argentina)' },
  { value: 'es_ES', label: 'Spanish (Spain)' },
  { value: 'es_MX', label: 'Spanish (Mexico)' },
  { value: 'sw', label: 'Swahili' },
  { value: 'sv', label: 'Swedish' },
  { value: 'ta', label: 'Tamil' },
  { value: 'te', label: 'Telugu' },
  { value: 'th', label: 'Thai' },
  { value: 'tr', label: 'Turkish' },
  { value: 'uk', label: 'Ukrainian' },
  { value: 'ur', label: 'Urdu' },
  { value: 'uz', label: 'Uzbek' },
  { value: 'vi', label: 'Vietnamese' },
  { value: 'zu', label: 'Zulu' },
];

// Validation constants
export const VALIDATION_LIMITS = {
  NAME_MAX_LENGTH: 512,
  BUTTON_TEXT_MAX_LENGTH: 20,
  MAX_BUTTONS_PER_TEMPLATE: 10,
  MAX_EDITS_PER_MONTH: 10,
  EDIT_TIME_LIMIT_HOURS: 24,
};
